import dayjs, { type Dayjs } from "dayjs";
import type { SchedulePick, ScheduleSlot } from "../../service/schedulePick.service";
import { LC_KEYS } from "../../utils/constants";
import { minutesSince } from "../CreateNoteModal/schedulePick.utils";

/** Rows the card shows once collapsed ("Show fewer"). */
export const VISIBLE_SLOTS = 3;
/**
 * The collapsed rows start half an hour back: Start is pressed a median of 19 minutes after the
 * booked slot (see ``client.schedule_pick``), so that is where "in the room" usually is.
 */
export const OPENS_BACK_MINUTES = 30;

/**
 * What the Start card offers: never a guess at one patient. It asks, and each answer is one click.
 *
 * - ``list``: "Who's in the room?" over the whole of today's schedule, in appointment order (see
 *   ``roomSlots``). The card opens expanded, scrolled so ``now`` (the first appointment not yet
 *   due) sits mid-list: clinics run late, so the earlier ones are a scroll up. Collapsed, it shows
 *   three rows from ``first``.
 * - ``dismissed``: the clinician closed the list (×). Start Encounter is the card, with Show,
 *   until the schedule moves on: the list comes back once its first visible appointment changes.
 * - ``none``: nobody booked today, everyone already documented, or no schedule: the plain Start
 *   Encounter card.
 */
export type QuickStartView =
  | { kind: "list"; slots: ScheduleSlot[]; first: number; now: number }
  | { kind: "dismissed"; slots: ScheduleSlot[]; first: number; now: number }
  | { kind: "none" };

/** Today's undocumented appointments in the server's order: the Recommended group, then the rest of the day. */
export const upcomingSlots = (pick: SchedulePick | null | undefined): ScheduleSlot[] => {
  if (!pick || pick.state !== "ready") return [];
  return [...pick.recommended, ...pick.up_next].filter((slot) => slot.client.id != null);
};

/**
 * The rows under "Who's in the room?": everyone on today's schedule, by appointment time (same
 * time: the clinician's own patients, then by name). A patient who already has a recording today
 * is listed like anyone else.
 */
export const roomSlots = (pick: SchedulePick | null | undefined): ScheduleSlot[] => {
  if (!pick || pick.state !== "ready") return [];
  return [...pick.recommended, ...pick.up_next, ...pick.documented]
    .filter((slot) => slot.client.id != null)
    .map((slot) => ({ slot, at: dayjs(slot.scheduled_at).valueOf() }))
    .sort(
      (a, b) =>
        a.at - b.at ||
        Number(a.slot.client.is_owned === false) - Number(b.slot.client.is_owned === false) ||
        (a.slot.client.name || "").localeCompare(b.slot.client.name || ""),
    )
    .map(({ slot }) => slot);
};

/** Where the card's three rows start: the first appointment from half an hour ago on, kept three from the end. */
export const firstVisibleIndex = (slots: ScheduleSlot[], now: Dayjs = dayjs()): number => {
  const opensAt = now.subtract(OPENS_BACK_MINUTES, "minute").valueOf();
  const index = slots.findIndex((slot) => dayjs(slot.scheduled_at).valueOf() >= opensAt);
  const last = Math.max(slots.length - VISIBLE_SLOTS, 0);
  return index < 0 ? last : Math.min(index, last);
};

/** Where "now" falls in the list: the first appointment not yet due (the list's length after the last). */
export const nowIndex = (slots: ScheduleSlot[], now: Dayjs = dayjs()): number => {
  const index = slots.findIndex((slot) => dayjs(slot.scheduled_at).valueOf() >= now.valueOf());
  return index < 0 ? slots.length : index;
};

export const quickStartView = (
  pick: SchedulePick | null | undefined,
  { dismissedId, now = dayjs() }: { dismissedId?: number | null; now?: Dayjs } = {},
): QuickStartView => {
  const slots = roomSlots(pick);
  if (slots.every((slot) => slot.documented_at != null)) return { kind: "none" };
  const first = firstVisibleIndex(slots, now);
  const at = nowIndex(slots, now);
  if (dismissedId != null && slots[first].client.id === dismissedId)
    return { kind: "dismissed", slots, first, now: at };
  return { kind: "list", slots, first, now: at };
};

/**
 * The first patient of the list the clinician closed (×), remembered for that clinic day. In
 * LC_KEYS, so logging out (or the session expiring) clears it: on a shared workstation the next
 * clinician never inherits it.
 */
export const loadDismissed = (day: string | null | undefined): number | null => {
  if (!day) return null;
  try {
    const stored = JSON.parse(localStorage.getItem(LC_KEYS.QUICK_START_DISMISSED) || "null") as {
      day?: string;
      id?: unknown;
    } | null;
    return stored && stored.day === day && typeof stored.id === "number" ? stored.id : null;
  } catch {
    return null;
  }
};

// The card is mounted twice (sidebar and the narrow layout's tab) and the app may be open in
// two tabs: every copy follows the stored dismissal, so × and Show apply to all of them at once.
const dismissedListeners = new Set<() => void>();
export const subscribeDismissed = (listener: () => void): (() => void) => {
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === LC_KEYS.QUICK_START_DISMISSED) listener();
  };
  dismissedListeners.add(listener);
  window.addEventListener("storage", onStorage);
  return () => {
    dismissedListeners.delete(listener);
    window.removeEventListener("storage", onStorage);
  };
};

export const saveDismissed = (day: string, id: number | null): void => {
  if (id == null) localStorage.removeItem(LC_KEYS.QUICK_START_DISMISSED);
  else localStorage.setItem(LC_KEYS.QUICK_START_DISMISSED, JSON.stringify({ day, id }));
  for (const listener of Array.from(dismissedListeners)) listener();
};

/**
 * ``quick_start_card_shown``: what the card offered, the denominator for every Quick Start rate
 * (``quick_start_started`` / shown is the take rate). ``key`` names one offer, so it is sent once
 * per page load however often the schedule polls; a new first row, or a new view of the card, is
 * a new offer. Null until the schedule has loaded. Ids, counts and reasons only.
 */
export const cardShownEvent = (
  pick: SchedulePick | null | undefined,
  view: QuickStartView,
): { key: string; properties: Record<string, unknown> } | null => {
  if (!pick) return null;
  const slots = view.kind === "none" ? [] : view.slots;
  const head = view.kind === "none" ? null : view.slots[view.first];
  // Why the plain card: the sheet's own state (no schedule, stale), or a day with nobody left to see.
  const noneReason =
    view.kind !== "none"
      ? null
      : pick.state !== "ready"
        ? pick.no_pick_reason ?? pick.state
        : pick.documented.length > 0
          ? "all_documented"
          : "nobody_booked_today";
  return {
    key: `${pick.day}:${view.kind}:${head?.client.id ?? noneReason}`,
    properties: {
      view: view.kind,
      candidates: slots.length,
      // Rows for patients who already have a recording today (a start there is a second one).
      recorded: slots.filter((slot) => slot.documented_at != null).length,
      none_reason: noneReason,
      pick_reason: pick.suggestion?.reason ?? null,
      confidence: pick.suggestion?.confidence ?? null,
      slot_offset_min: head ? minutesSince(head.scheduled_at) : null,
      upcoming: upcomingSlots(pick).length,
      documented: pick.documented.length,
      schedule_size: pick.schedule_size,
    },
  };
};
